import type { HydraulicType } from '@/types/hydraulic'
import { CREATE_PIPE_ID, DRAFT_ID_START } from './constants'
import type { DraftPipe, HydraulicTreeRow } from './types'

/**
 * True for a staged create.
 *
 * Tested as a NUMBER, not by a leading '-': pipe ids arrive from the API as
 * numbers, and this page already uses small negative ids for the create chip
 * row, which is not a draft.
 */
export const isDraftId = (id: number) => Number(id) <= DRAFT_ID_START

/**
 * Drops a pipe from the top level when the same pipe also appears nested
 * inside another.
 *
 * Searching returns every match as a root along with its own subtree, so a pipe
 * whose parent also matched comes back twice — once in place and once promoted.
 * Rendering both repeats the row and gives two rows the same id. The nested
 * copy is kept because it shows where the pipe actually sits.
 */
export const dropPromotedDuplicates = (
  items: HydraulicType[],
): HydraulicType[] => {
  const nestedIds = new Set<number>()

  const collect = (list: HydraulicType[]) => {
    list.forEach((item) => {
      ;(item.subPipes ?? []).forEach((sub) => {
        nestedIds.add(sub.id)
        collect([sub])
      })
    })
  }

  collect(items)
  return items.filter((item) => !nestedIds.has(item.id))
}

export const flattenHydraulicData = (
  rows: HydraulicType[],
): HydraulicTreeRow[] => {
  const result: HydraulicTreeRow[] = []

  const flatten = (items: HydraulicType[], parentPath: string[] = []) => {
    items.forEach((item) => {
      const currentPath = [...parentPath, `${item.id}`]
      result.push({ ...item, path: currentPath })

      if (item.subPipes?.length > 0) {
        flatten(item.subPipes, currentPath)
      }
    })
  }
  flatten(rows)
  return result
}

/** The tree node a staged draft renders as, at `path`. */
export const draftTreeRow = (
  draft: DraftPipe,
  path: string[],
): HydraulicTreeRow =>
  ({
    id: draft.id,
    description: draft.description,
    capacity: draft.capacity,
    currentFlow: 0,
    localFlow: 0,
    flowRuleId: null,
    parentId: draft.parentId,
    level: draft.level,
    subPipes: [],
    status: '',
    sensors: 0,
    valves: 0,
    expectedFlow: 0,
    measuredFlow: 0,
    observedIdleFlow: 0,
    waterConsumption: {
      lastHourWaterConsumption: 0,
      lastDayWaterConsumption: 0,
      lastWeekWaterConsumption: 0,
      lastMonthWaterConsumption: 0,
    },
    path,
    interfaceId: draft.interfaceId,
  }) as any

/**
 * The "+ Add New Mainline" chip row, appended as the last row of the tree
 * (rather than pinned to the viewport bottom) so it flows right after the last
 * pipe instead of floating at the bottom with a gap above it.
 */
export const createPipeRow = (interfaceId: number): HydraulicTreeRow =>
  ({
    id: CREATE_PIPE_ID,
    description: '__CREATE_PIPE__',
    capacity: 0,
    currentFlow: 0,
    localFlow: 0,
    flowRuleId: null,
    parentId: 0,
    level: 0,
    subPipes: [],
    status: '',
    sensors: 0,
    valves: 0,
    expectedFlow: 0,
    measuredFlow: 0,
    observedIdleFlow: 0,
    waterConsumption: {
      lastHourWaterConsumption: 0,
      lastDayWaterConsumption: 0,
      lastWeekWaterConsumption: 0,
      lastMonthWaterConsumption: 0,
    },
    path: ['__CREATE_PIPE__'],
    isCreateRow: true,
    interfaceId,
  }) as any

export const rowKey = (row: HydraulicTreeRow) =>
  row.isCreateRow
    ? `create-${row.id}-${row.parentId}`
    : // Keyed by position, not just id. Search returns a matching sub-pipe as a
      // root, and with an id-only key the pipe was left stranded at the root
      // once the search was cleared.
      (row.path ?? [String(row.id)]).join('/')
